// EQ DSP utilities: create and wire master high-pass and low-pass filters,
// plus helpers to clamp and set frequencies safely.

export interface MasterEqNodes {
  hp: BiquadFilterNode;
  lp: BiquadFilterNode;
}

export const getNyquist = (ctx: BaseAudioContext) => (ctx.sampleRate || 48000) / 2;

export function createMasterEQ(
  ctx: AudioContext,
  opts?: { hpfHz?: number; lpfHz?: number; q?: number }
): MasterEqNodes {
  const q = clampQ(opts?.q ?? 0.707);
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.Q.value = q;
  try { hp.frequency.value = clampHpfHz(opts?.hpfHz ?? 20, ctx); } catch {}

  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.Q.value = q;
  try { lp.frequency.value = clampLpfHz(opts?.lpfHz ?? 20000, ctx); } catch {}

  return { hp, lp };
}

export function wireMasterEQ(
  ctx: AudioContext,
  masterGain: GainNode,
  nodes: MasterEqNodes,
  analyser?: AnalyserNode | null
) {
  const { safeConnectCtx } = require('./mixEngine/bufferTransport/ctxUtil');
  // masterGain -> HPF -> LPF -> destination, and LPF -> analyser (post-EQ)
  try { masterGain.disconnect(); } catch {}
  try { nodes.hp.disconnect(); } catch {}
  try { nodes.lp.disconnect(); } catch {}
  try { safeConnectCtx(ctx, masterGain, nodes.hp); } catch {}
  try { safeConnectCtx(ctx, nodes.hp, nodes.lp); } catch {}
  try { safeConnectCtx(ctx, nodes.lp, ctx.destination); } catch {}
  if (analyser) {
    try { safeConnectCtx(ctx, nodes.lp, analyser); } catch {}
  }
}

export function clampLpfHz(hz: number, ctx?: BaseAudioContext): number {
  const ny = ctx ? getNyquist(ctx) : 24000;
  return Math.max(20, Math.min(ny, hz));
}

export function clampHpfHz(hz: number, ctx?: BaseAudioContext): number {
  const ny = ctx ? getNyquist(ctx) : 24000;
  // HPF shouldn’t exceed Nyquist and shouldn’t be below 20 Hz
  return Math.max(20, Math.min(ny, hz));
}

export function setLpfFreq(lp: BiquadFilterNode | null, hz: number, ctx?: BaseAudioContext, rampMs = 30) {
  if (!lp) return; try {
    const tctx = ctx as AudioContext | undefined;
    const target = clampLpfHz(hz, ctx);
    if (tctx) {
      const now = tctx.currentTime;
      try { lp.frequency.cancelScheduledValues(now); } catch {}
      try { lp.frequency.setValueAtTime(lp.frequency.value, now); lp.frequency.linearRampToValueAtTime(target, now + Math.max(0.01, rampMs / 1000)); } catch { lp.frequency.value = target; }
    } else {
      lp.frequency.value = target;
    }
  } catch {}
}

export function setHpfFreq(hp: BiquadFilterNode | null, hz: number, ctx?: BaseAudioContext, rampMs = 30) {
  if (!hp) return; try {
    const tctx = ctx as AudioContext | undefined;
    const target = clampHpfHz(hz, ctx);
    if (tctx) {
      const now = tctx.currentTime;
      try { hp.frequency.cancelScheduledValues(now); } catch {}
      try { hp.frequency.setValueAtTime(Math.max(1, hp.frequency.value), now); hp.frequency.exponentialRampToValueAtTime(Math.max(1, target), now + Math.max(0.01, rampMs / 1000)); } catch { hp.frequency.value = target; }
    } else {
      hp.frequency.value = target;
    }
  } catch {}
}

// Typical useful range for Q in gentle music-bus HPF/LPF: 0.5 – 2.0
export function clampQ(q: number): number {
  if (!isFinite(q)) return 0.707;
  return Math.max(0.5, Math.min(2.0, q));
}

export function setEqQ(nodes: MasterEqNodes | null, q: number, ctx?: BaseAudioContext, rampMs = 30) {
  if (!nodes) return; const qq = clampQ(q);
  try {
    const tctx = ctx as AudioContext | undefined;
    if (tctx) {
      const now = tctx.currentTime;
      try { nodes.hp.Q.cancelScheduledValues(now); } catch {}
      try { nodes.lp.Q.cancelScheduledValues(now); } catch {}
      try { nodes.hp.Q.setValueAtTime(nodes.hp.Q.value, now); nodes.lp.Q.setValueAtTime(nodes.lp.Q.value, now); nodes.hp.Q.linearRampToValueAtTime(qq, now + Math.max(0.01, rampMs / 1000)); nodes.lp.Q.linearRampToValueAtTime(qq, now + Math.max(0.01, rampMs / 1000)); } catch { nodes.hp.Q.value = qq; nodes.lp.Q.value = qq; }
    } else {
      nodes.hp.Q.value = qq; nodes.lp.Q.value = qq;
    }
  } catch {}
}
